import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for checking organization-level permissions based on user role.
 *
 * Permission Logic:
 * - superAdmin: Full access to all organizations
 * - organizationAdmin: Can edit only organizations in their organizationIds array
 * - viewer: Read-only access, no editing capabilities
 */
export const useOrganizationPermissions = () => {
  const { currentUser } = useAuth();

  const isSuperAdmin = useMemo(() => {
    return currentUser?.role === 'superAdmin';
  }, [currentUser?.role]);

  const isOrgAdmin = useMemo(() => {
    return currentUser?.role === 'organizationAdmin';
  }, [currentUser?.role]);

  const isViewer = useMemo(() => {
    return currentUser?.role === 'viewer';
  }, [currentUser?.role]);

  /**
   * Check if user can edit a specific organization.
   * SuperAdmin can edit any org, OrgAdmin can only edit orgs in their organizationIds.
   */
  const canEditOrganization = useCallback((organizationId: number): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'superAdmin') return true;
    if (currentUser.role === 'organizationAdmin') {
      return currentUser.organizationIds?.includes(organizationId) ?? false;
    }
    return false;
  }, [currentUser]);

  /**
   * Check if user can manage staff at a specific organization.
   * Same logic as canEditOrganization - if you can edit the org, you can manage staff.
   */
  const canManageStaff = useCallback((organizationId: number): boolean => {
    return canEditOrganization(organizationId);
  }, [canEditOrganization]);

  /**
   * Only superAdmin can add new schools to an LEA.
   */
  const canAddSchools = useMemo(() => {
    return currentUser?.role === 'superAdmin';
  }, [currentUser?.role]);

  /**
   * Only superAdmin can delete (soft-delete/close) schools.
   */
  const canDeleteSchools = useMemo(() => {
    return currentUser?.role === 'superAdmin';
  }, [currentUser?.role]);

  /**
   * Check if user can enter edit mode for an organization.
   * Alias for canEditOrganization for semantic clarity.
   */
  const canEnterEditMode = useCallback((organizationId: number): boolean => {
    return canEditOrganization(organizationId);
  }, [canEditOrganization]);

  return {
    // Role flags
    isSuperAdmin,
    isOrgAdmin,
    isViewer,

    // Permission checks
    canEditOrganization,
    canManageStaff,
    canAddSchools,
    canDeleteSchools,
    canEnterEditMode,

    // Current user for additional checks if needed
    currentUser
  };
};
